import { FunctionComponent } from 'react';
import 'chart.js/auto';
import { Doughnut } from 'react-chartjs-2';
import type { ScriptableContext } from 'chart.js';
import styles from './GaugeChart.module.scss';

export type GaugeChartProps = {
  value: number;
  max?: number;
  // Gradient stops for the filled arc, light to dark
  gradient?: [string, string];
  trackColor?: string;
  width?: number;
};

type FC = FunctionComponent<GaugeChartProps>;

const GaugeChart: FC = ({
  value,
  max = 100,
  gradient = ['#7FB0FF', '#2F82FF'],
  trackColor = 'rgba(45, 45, 45, 0.08)',
  width = 140,
}: GaugeChartProps) => {
  const clamped = Math.min(Math.max(value, 0), max);

  function arcGradient(context: ScriptableContext<'doughnut'>): string | CanvasGradient {
    const { chartArea, ctx } = context.chart;
    if (!chartArea) {
      return gradient[1];
    }
    const fill = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
    fill.addColorStop(0, gradient[0]);
    fill.addColorStop(1, gradient[1]);
    return fill;
  }

  return (
    <div className={styles.container} style={{ width, height: width / 2 + 16 }} data-testid="gauge-chart">
      <Doughnut
        data={{
          datasets: [
            {
              data: [clamped, max - clamped],
              backgroundColor: (context: ScriptableContext<'doughnut'>) =>
                context.dataIndex === 0 ? arcGradient(context) : trackColor,
              borderColor: 'transparent',
              borderRadius: 10,
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          cutout: '82%',
          circumference: 180,
          rotation: 270,
          events: [],
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          },
        }}
      />
      <div className={styles.valueOverlay}>
        <span className={styles.value}>{Math.round(clamped)}</span>
        <span className={styles.max}> / {max}</span>
      </div>
    </div>
  );
};

export default GaugeChart;
